import React, { useState } from 'react';
import { Info } from 'lucide-react';
import { OwnerButton, OwnerCard, OwnerShell } from '../OwnerShell';
import { SelectField, TextField } from '../OwnerFields';
import { FlowSteps } from '../FlowSteps';
import { useOwnerService } from '../../../contexts/OwnerServiceContext';
import type { OwnerNav, VehicleDraft } from '../../../types/owner';
export function OwnerVehicleForm({
  nav
}: {
  nav: OwnerNav;
}) {
  const {
    applications
  } = useOwnerService();
  const [f, setF] = useState<VehicleDraft>(nav.draft);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const cng = f.type === 'cng';
  const set = (k: keyof VehicleDraft) => (v: string) => setF(s => ({
    ...s,
    [k]: v
  }));
  const existing = applications.find(a => a.vehicleReg === f.reg.trim() && a.status !== 'বাতিল');
  const next = () => {
    const e: Record<string, string> = {};
    if (!f.reg.trim()) e.reg = 'রেজিস্ট্রেশন নম্বর আবশ্যক';
    if (!/^[A-Z0-9]{10,17}$/.test(f.chassis.trim())) e.chassis = 'চেসিস নম্বর ইংরেজি অক্ষর ও সংখ্যায় ১০–১৭ অক্ষর';
    if (f.engine.trim().length < 6) e.engine = 'সঠিক ইঞ্জিন নম্বর দিন';
    if (!f.brand.trim()) e.brand = 'ব্র্যান্ড লিখুন';
    setErrors(e);
    if (Object.keys(e).length) return nav.showToast('লাল চিহ্নিত ঘরগুলো সংশোধন করুন');
    nav.setDraft(f);
    nav.go('documents');
  };
  return <OwnerShell nav={nav} title="গাড়ির নিবন্ধন তথ্য" subtitle={cng ? 'সিএনজি / অটোরিকশা' : 'মোটরসাইকেল'} bodyClassName="p-0" footer={<div className="grid grid-cols-3 gap-2" data-id="element-2135" data-mp="2ob4n1ey3rkcp">
          <OwnerButton variant="ghost" onClick={nav.back} className="!px-2 !text-sm" data-id="element-2136" data-mp="2jb07br50qtnj">পূর্ববর্তী</OwnerButton>
          <OwnerButton variant="outline" onClick={() => {
      nav.setDraft(f);
      nav.showToast('খসড়া সংরক্ষণ করা হয়েছে');
    }} className="!px-2 !text-sm" data-id="element-2137" data-mp="2ec5quvuixh6l">খসড়া সংরক্ষণ</OwnerButton>
          <OwnerButton onClick={next} className="!px-2 !text-sm" data-id="element-2138" data-mp="17tqpta5gabov">পরবর্তী</OwnerButton>
        </div>} data-id="element-2134" data-mp="1vjb95u5inayo">
      <FlowSteps current={1} data-id="element-2139" data-mp="12z375rx4obi7" />
      <div className="space-y-3 px-4 py-4" data-id="element-2140" data-mp="3r2fml30krwsx">
        <p className="text-xs font-semibold text-slate-500" data-id="element-2141" data-mp="3r3gw331y0b8j">ধাপ ১ — মৌলিক তথ্য</p>
        {existing && <p className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-[11.5px] leading-relaxed text-amber-800" data-id="element-2142" data-mp="3v26j7ncs975d">
            <Info className="mt-0.5 h-4 w-4 shrink-0" data-id="element-2143" data-mp="3m330ikhj2tre" />
            এই নম্বরের একটি আবেদন ({existing.id}) ইতোমধ্যে রয়েছে। পুনরায় জমা দিলে ডিএমপি ডুপ্লিকেট হিসেবে যাচাই করবে।
          </p>}
        <OwnerCard data-id="element-2144" data-mp="3c5yc0e07mm7f">
          <div className="space-y-3" data-id="element-2145" data-mp="3c6ggfxx106qf">
            <SelectField id="v-cat" label="গাড়ির ধরন" required value={f.category} options={cng ? ['সিএনজি অটোরিকশা', 'ব্যাটারিচালিত অটোরিকশা'] : ['মোটরসাইকেল']} onChange={set('category')} data-id="element-2146" data-mp="3c7j662im434r" />
            <TextField id="v-reg" label="রেজিস্ট্রেশন নম্বর" required value={f.reg} onChange={set('reg')} error={errors.reg} hint={cng ? 'যেমন: ঢাকা-থ-১১-১২৩৪' : 'যেমন: ঢাকা মেট্রো-ল-৪৫-৬৭৮৯'} data-id="element-2147" data-mp="33dctmz0hqhj4" />
            <TextField id="v-rd" label="রেজিস্ট্রেশনের তারিখ" required value={f.regDate} onChange={set('regDate')} hint="দিন/মাস/বছর" data-id="element-2148" data-mp="2te5imih8ayz6" />
            <div className="grid grid-cols-2 gap-3" data-id="element-2149" data-mp="25rjtaboupgw5">
              <TextField id="v-brand" label="গাড়ির ব্র্যান্ড" required value={f.brand} onChange={set('brand')} error={errors.brand} data-id="element-2150" data-mp="25t4kpsfvcjps" />
              <TextField id="v-model" label="মডেল" required value={f.model} onChange={set('model')} data-id="element-2151" data-mp="2joyndtq1ucug" />
              <TextField id="v-year" label="তৈরির সাল" value={f.year} onChange={set('year')} data-id="element-2152" data-mp="2srzqdljmbntw" />
              <TextField id="v-color" label="গাড়ির রং" value={f.color} onChange={set('color')} data-id="element-2153" data-mp="2spn08l3pi75r" />
            </div>
            <TextField id="v-ch" label="চেসিস নম্বর" required mono value={f.chassis} onChange={v => set('chassis')(v.toUpperCase())} error={errors.chassis} data-id="element-2154" data-mp="32hjbkxusztrx" />
            <TextField id="v-en" label="ইঞ্জিন নম্বর" required mono value={f.engine} onChange={v => set('engine')(v.toUpperCase())} error={errors.engine} data-id="element-2155" data-mp="2xjhqk7dthkdt" />
            <div className="grid grid-cols-2 gap-3" data-id="element-2156" data-mp="37hounpn0c7ye">
              <SelectField id="v-fuel" label="জ্বালানির ধরন" value={f.fuel} options={cng ? ['CNG', 'বিদ্যুৎ (ব্যাটারি)', 'LPG'] : ['পেট্রোল', 'অকটেন', 'বিদ্যুৎ (ব্যাটারি)']} onChange={set('fuel')} data-id="element-2157" data-mp="37esz27ymtvd4" />
              <SelectField id="v-own" label="মালিকানার ধরন" value={f.ownership} options={['একক মালিকানা', 'যৌথ মালিকানা', 'প্রতিষ্ঠান', 'কিস্তি/লিজ']} onChange={set('ownership')} data-id="element-2158" data-mp="19r8o5nv2k9x3" />
            </div>
          </div>
        </OwnerCard>
      </div>
    </OwnerShell>;
}